import { createContext, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n, { MyLanguages } from './i18n';
import { isPrerender, langFromPath, localizedPath, preferredLanguage, rememberLanguage } from './seo/lang';

const LanguageContext = createContext();

// The language is part of the URL (seo/lang.ts). Picking one navigates to the same
// page under that language's prefix instead of switching in place.
export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlLanguage = langFromPath(location.pathname);
  const selectedLanguage = MyLanguages.find((lang) => lang.code === urlLanguage.code) || MyLanguages[0];

  useEffect(() => {
    if (i18n.language !== selectedLanguage.code) i18n.changeLanguage(selectedLanguage.code);
  }, [selectedLanguage.code]);

  // Once per page load: a visitor on an unprefixed (English) URL whose saved choice
  // or browser language is German, French or Italian goes to that version. The
  // prerender and crawlers carry neither, so they stay on the English URL.
  useEffect(() => {
    if (isPrerender() || urlLanguage.prefix) return;
    const preferred = preferredLanguage();
    if (preferred && preferred.prefix) {
      navigate(localizedPath(location.pathname, preferred.code) + location.search + location.hash, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLanguage = (language) => {
    rememberLanguage(language.code);
    navigate(localizedPath(location.pathname, language.code) + location.search + location.hash);
  };

  return (
    <LanguageContext.Provider value={{ selectedLanguage, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
